//# sourceMappingURL=attribute.mjs.map
/// <reference types="./attribute.d.mts" />
import * as $form_data from "../../../gleam_fetch/gleam/fetch/form_data.mjs";
import * as $dynamic from "../../../gleam_stdlib/gleam/dynamic.mjs";
import * as $option from "../../../gleam_stdlib/gleam/option.mjs";
import * as $string from "../../../gleam_stdlib/gleam/string.mjs";
import * as $unsafe from "../../../redraw/redraw/internal/unsafe.mjs";
import * as $ref from "../../../redraw/redraw/ref.mjs";
import { CustomType as $CustomType } from "../../gleam.mjs";
import { innerHTML as inner_html, convertStyle as convert_style } from "./attribute.ffi.mjs";

export { inner_html };

class Attribute extends $CustomType {
  constructor(key, content) {
    super();
    this.key = key;
    this.content = content;
  }
}

export class Ltr extends $CustomType {}
export const Dir$Ltr = () => new Ltr();
export const Dir$isLtr = (value) => value instanceof Ltr;

export class Rtl extends $CustomType {}
export const Dir$Rtl = () => new Rtl();
export const Dir$isRtl = (value) => value instanceof Rtl;

export class Yes extends $CustomType {}
export const Translate$Yes = () => new Yes();
export const Translate$isYes = (value) => value instanceof Yes;

export class No extends $CustomType {}
export const Translate$No = () => new No();
export const Translate$isNo = (value) => value instanceof No;

/**
 * Generic attribute. `key` will be the key in the HTML attributes as-is,
 * while content can be anything. `content` will be converted by React to
 * strings, so be careful to not send complicated data structure, but booleans,
 * strings numbers, etc. are plainly supported.
 *
 * [Documentation](https://developer.mozilla.org/docs/Web/HTML/Reference/Attributes)
 */
export function attribute(key, content) {
  let content$1 = $unsafe.coerce(content);
  return new Attribute(key, content$1);
}

/**
 * [Documentation](https://developer.mozilla.org/docs/Web/API/HTMLAnchorElement/href)
 */
export function href(url) {
  return attribute("href", url);
}

/**
 * [Documentation](https://developer.mozilla.org/docs/Web/API/HTMLAnchorElement/target)
 */
export function target(value) {
  return attribute("target", value);
}

/**
 * [Documentation](https://developer.mozilla.org/docs/Web/API/HTMLMediaElement/src)
 */
export function src(value) {
  return attribute("src", value);
}

/**
 * To follow Lustre pattern, `class` is additive. When using multiple `class`
 * on a single element, they will all be applied on that element.
 *
 * [Documentation](https://developer.mozilla.org/docs/Web/HTML/Global_attributes/class)
 */
export function class$(value) {
  return attribute("className", value);
}

/**
 * [Documentation](https://developer.mozilla.org/docs/Web/API/HTMLMediaElement/alt)
 */
export function alt(value) {
  return attribute("alt", value);
}

/**
 * Overrides the innerHTML property of the DOM node and displays the passed
 * HTML inside. This should be used with extreme caution! If the HTML inside
 * isn’t trusted (for example, if it’s based on user data), you risk
 * introducing an XSS vulnerability. \
 * [Documentation](https://react.dev/reference/react-dom/components/common#dangerously-setting-the-inner-html)
 */
export function dangerously_set_inner_html(inner_html) {
  return attribute("dangerouslySetInnerHTML", inner_html);
}

/**
 * A ref object from `redraw.use_ref`. Your ref will be filled with the DOM
 * element for this node. Contrarily to JS React, when using a Ref, you're
 * forced to use an optional type here. Because when using this function, you
 * want to get a reference from a real DOM node, meaning at the initialization
 * of the reference, you won't have any data. Use `ref_` when you want full
 * control over the ref you send to the Component. \
 * [Documentation](https://react.dev/reference/react-dom/components/common#manipulating-a-dom-node-with-a-ref)
 */
export function ref(ref) {
  return attribute(
    "ref",
    (dom_ref) => { return $ref.assign(ref, new $option.Some(dom_ref)); },
  );
}

/**
 * A ref callback function. The callback will be provided with the DOM element
 * for this node. Use this function to get control on the ref provided by the
 * DOM node or the component. \
 * [Documentation](https://react.dev/reference/react-dom/components/common#manipulating-a-dom-node-with-a-ref)
 */
export function ref_(ref) {
  return attribute("ref", ref);
}

/**
 * If `True`, suppresses the warning that React shows for elements that both
 * have `children` and `content_editable(True)` (which normally do not work
 * together). Use this if you’re building a text input library that manages
 * the `contentEditable` content manually.
 */
export function suppress_content_editable_warning(value) {
  return attribute("suppressContentEditableWarning", value);
}

/**
 * If you use [server rendering](https://react.dev/reference/react-dom/server),
 * normally there is a warning when the server and the client render different
 * content. In some rare cases (like timestamps), it is very hard or impossible
 * to guarantee an exact match. If you set suppressHydrationWarning to true,
 * React will not warn you about mismatches in the attributes and the content
 * of that element. It only works one level deep, and is intended to be used
 * as an escape hatch. Don’t overuse it.
 * [Read about suppressing hydration errors.](https://react.dev/reference/react-dom/client/hydrateRoot#suppressing-unavoidable-hydration-mismatch-errors)
 */
export function suppress_hydration_warning(value) {
  return attribute("suppressHydrationWarning", value);
}

/**
 * [Documentation](https://developer.mozilla.org/docs/Web/HTML/Global_attributes/style)
 */
export function style(styles) {
  return attribute("style", convert_style(styles));
}

/**
 * [Documentation](https://developer.mozilla.org/docs/Web/HTML/Global_attributes/accesskey)
 */
export function access_key(value) {
  return attribute("accessKey", value);
}

/**
 * [Documentation](https://developer.mozilla.org/docs/Web/HTML/Global_attributes/autocapitalize)
 */
export function auto_capitalize(value) {
  return attribute("autoCapitalize", value);
}

/**
 * [Documentation](https://developer.mozilla.org/docs/Web/HTML/Global_attributes/autofocus)
 */
export function auto_focus(value) {
  return attribute("autoFocus", value);
}

/**
 * Alias of `class`. \
 * [Documentation](https://developer.mozilla.org/docs/Web/HTML/Global_attributes/class)
 */
export function class_name(value) {
  return attribute("className", value);
}

/**
 * Specifies the value of a metadata name defined by the `<meta>` `name`
 * attribute. It takes a string as its value, and the expected syntax varies
 * depending on the `name` value used. \
 * [Documentation](https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Attributes/content)
 */
export function content(value) {
  return attribute("content", value);
}

/**
 * If true, the browser lets the user edit the rendered element directly.
 * This is used to implement rich text input libraries like Lexical. React
 * warns if you try to pass React children to an element with
 * `content_editable(True)` because React will not be able to update its content
 * after user edits. \
 * [Documentation](https://developer.mozilla.org/docs/Web/API/HTML/Global_attributes/contenteditable)
 */
export function content_editable(value) {
  return attribute("contentEditable", value);
}

/**
 * [Documentation](https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Attributes/crossorigin)
 */
export function cross_origin(value) {
  return attribute("crossOrigin", value);
}

/**
 * Data attributes let you attach some string data to the element, for example
 * `data("fruit", "banana")`. In React, they are not commonly used because you
 * would usually read data from props or state instead. \
 * [Documentation](https://developer.mozilla.org/docs/Web/HTML/Global_attributes/data-*)
 */
export function data(key, value) {
  return attribute("data-" + key, value);
}

/**
 * Specifies the text direction of the element. \
 * [Documentation](https://developer.mozilla.org/docs/Web/HTML/Global_attributes/dir)
 */
export function dir(value) {
  let _block;
  if (value instanceof Ltr) {
    _block = "ltr";
  } else {
    _block = "rtl";
  }
  let value$1 = _block;
  return attribute("dir", value$1);
}

/**
 * [Documentation](https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Attributes/dirname)
 */
export function dirname(value) {
  return attribute("dirName", value);
}

/**
 * Specifies whether the element is draggable. Part of HTML Drag and Drop API. \
 * [Documentation](https://developer.mozilla.org/docs/Web/HTML/Global_attributes/draggable)
 */
export function draggable(value) {
  return attribute("draggable", value);
}

/**
 * Specifies which action to present for the enter key on virtual keyboards. \
 * [Documentation](https://developer.mozilla.org/docs/Web/HTML/Global_attributes/enterkeyhint)
 */
export function enter_key_hint(value) {
  return attribute("enterKeyHint", value);
}

/**
 * [Documentation](https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Global_attributes/exportparts)
 */
export function export_parts(value) {
  return attribute("exportParts", $string.join(value, ", "));
}

/**
 * [Documentation](https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Attributes/elementtiming)
 */
export function element_timing(value) {
  return attribute("elementTiming", value);
}

/**
 * For <label> and <output>, lets you associate the label with some control.
 * Same as for HTML attribute. \
 * [Documentation](https://developer.mozilla.org/docs/Web/HTML/Attributes/for)
 */
export function for$(value) {
  return attribute("htmlFor", value);
}

/**
 * Alias of `for`. \
 * [Documentation](https://developer.mozilla.org/docs/Web/HTML/Attributes/for)
 */
export function html_for(value) {
  return attribute("htmlFor", value);
}

/**
 * Specifies whether the element should be hidden. \
 * [Documentation](https://developer.mozilla.org/docs/Web/HTML/Global_attributes/hidden)
 */
export function hidden(value) {
  return attribute("hidden", value);
}

/**
 * For `<input type="email">`, `<input type="file">` and `<select>`, indicates
 * if it accepts one or more values. \
 * [Documentation](https://developer.mozilla.org/docs/Web/HTML/Reference/multiple)
 */
export function multiple(value) {
  return attribute("multiple", value);
}

/**
 * Specifies a unique identifier for this element, which can be used to find
 * it later or connect it with other elements. Generate it with useId to avoid
 * clashes between multiple instances of the same component. \
 * [Documentation](https://developer.mozilla.org/docs/Web/HTML/Global_attributes/id)
 */
export function id(value) {
  return attribute("id", value);
}

/**
 * [Documentation](https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Global_attributes/inert)
 */
export function inert(value) {
  return attribute("inert", value);
}

/**
 * If specified, the component will behave like a custom element. \
 * [Documentation](https://developer.mozilla.org/docs/Web/HTML/Global_attributes/is)
 */
export function is(value) {
  return attribute("is", value);
}

/**
 * Specifies what kind of keyboard to display (for example, text, number or telephone). \
 * [Documentation](https://developer.mozilla.org/docs/Web/HTML/Global_attributes/inputmode)
 */
export function input_mode(value) {
  return attribute("inputMode", value);
}

/**
 * [Documentation](https://developer.mozilla.org/docs/Web/HTML/Global_attributes/itemid)
 */
export function item_id(value) {
  return attribute("itemId", value);
}

/**
 * Specifies which property the element represents for structured data crawlers. \
 * [Documentation](https://developer.mozilla.org/docs/Web/HTML/Global_attributes/itemprop)
 */
export function item_prop(value) {
  return attribute("itemProp", value);
}

/**
 * [Documentation](https://developer.mozilla.org/docs/Web/HTML/Global_attributes/itemref)
 */
export function item_ref(value) {
  return attribute("itemRef", value);
}

/**
 * [Documentation](https://developer.mozilla.org/docs/Web/HTML/Global_attributes/itemscope)
 */
export function item_scope(value) {
  return attribute("itemScope", value);
}

/**
 * [Documentation](https://developer.mozilla.org/docs/Web/HTML/Global_attributes/itemtype)
 */
export function item_type(value) {
  return attribute("itemType", value);
}

/**
 * Specifies the language of the element. \
 * [Documentation](https://developer.mozilla.org/docs/Web/HTML/Global_attributes/lang)
 */
export function lang(value) {
  return attribute("lang", value);
}

/**
 * Specifies the element role explicitly for assistive technologies.
 */
export function role(value) {
  return attribute("role", value);
}

/**
 * Specifies the slot name when using shadow DOM. \
 * [Documentation](https://developer.mozilla.org/docs/Web/HTML/Global_attributes/slot)
 */
export function slot(value) {
  return attribute("slot", value);
}

/**
 * If explicitly set to true or false, enables or disables spellchecking. \
 * [Documentation](https://developer.mozilla.org/docs/Web/HTML/Global_attributes/spellcheck)
 */
export function spell_check(value) {
  return attribute("spellCheck", value);
}

/**
 * Overrides the default Tab button behavior. Avoid using values other than -1 and 0. \
 * [Documentation](https://developer.mozilla.org/docs/Web/HTML/Global_attributes/tabindex)
 */
export function tab_index(value) {
  return attribute("tabIndex", value);
}

/**
 * Specifies the tooltip text for the element. \
 * [Documentation](https://developer.mozilla.org/docs/Web/HTML/Global_attributes/title)
 */
export function title(value) {
  return attribute("title", value);
}

/**
 * Passing No excludes the element content from being translated. \
 * [Documentation](https://developer.mozilla.org/docs/Web/HTML/Global_attributes/translate)
 */
export function translate(value) {
  let _block;
  if (value instanceof Yes) {
    _block = "yes";
  } else {
    _block = "no";
  }
  let value$1 = _block;
  return attribute("translate", value$1);
}

/**
 * Type of an `input`. Value can be
 * [`"button"`](https://developer.mozilla.org/docs/Web/HTML/Element/input/button),
 * [`"checkbox"`](https://developer.mozilla.org/docs/Web/HTML/Element/input/checkbox),
 * [`"color"`](https://developer.mozilla.org/docs/Web/HTML/Element/input/color),
 * [`"date"`](https://developer.mozilla.org/docs/Web/HTML/Element/input/date),
 * [`"datetime-local"`](https://developer.mozilla.org/docs/Web/HTML/Element/input/datetime-local),
 * [`"email"`](https://developer.mozilla.org/docs/Web/HTML/Element/input/email),
 * [`"file"`](https://developer.mozilla.org/docs/Web/HTML/Element/input/file),
 * [`"hidden"`](https://developer.mozilla.org/docs/Web/HTML/Element/input/hidden),
 * [`"image"`](https://developer.mozilla.org/docs/Web/HTML/Element/input/image),
 * [`"month"`](https://developer.mozilla.org/docs/Web/HTML/Element/input/month),
 * [`"number"`](https://developer.mozilla.org/docs/Web/HTML/Element/input/number),
 * [`"password"`](https://developer.mozilla.org/docs/Web/HTML/Element/input/password),
 * [`"radio"`](https://developer.mozilla.org/docs/Web/HTML/Element/input/radio),
 * [`"range"`](https://developer.mozilla.org/docs/Web/HTML/Element/input/range),
 * [`"reset"`](https://developer.mozilla.org/docs/Web/HTML/Element/input/reset),
 * [`"search"`](https://developer.mozilla.org/docs/Web/HTML/Element/input/search),
 * [`"submit"`](https://developer.mozilla.org/docs/Web/HTML/Element/input/submit),
 * [`"tel"`](https://developer.mozilla.org/docs/Web/HTML/Element/input/tel),
 * [`"text"`](https://developer.mozilla.org/docs/Web/HTML/Element/input/text),
 * [`"time"`](https://developer.mozilla.org/docs/Web/HTML/Element/input/time),
 * [`"url"`](https://developer.mozilla.org/docs/Web/HTML/Element/input/url), or
 * [`"week"`](https://developer.mozilla.org/docs/Web/HTML/Element/input/week).
 */
export function type_(name) {
  return attribute("type", name);
}

/**
 * [Input Documentation](https://developer.mozilla.org/docs/Web/HTML/Element/input#value) \
 * [Button Documentation](https://developer.mozilla.org/docs/Web/HTML/Element/button#value)
 */
export function value(val) {
  return attribute("value", val);
}

/**
 * [Documentation](https://developer.mozilla.org/docs/Web/HTML/Element/input#checked)
 */
export function checked(is_checked) {
  return attribute("checked", is_checked);
}

/**
 * [Documentation](https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Global_attributes/part)
 */
export function part(values) {
  return attribute("part", $string.join(values, " "));
}

/**
 * [Documentation](https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Global_attributes/popover)
 */
export function popover(value) {
  return attribute("popover", value);
}

/**
 * [Documentation](https://developer.mozilla.org/docs/Web/HTML/Attributes/placeholder)
 */
export function placeholder(text) {
  return attribute("placeholder", text);
}

/**
 * [Documentation](https://developer.mozilla.org/docs/Web/HTML/Element/option#selected)
 */
export function selected(is_selected) {
  return attribute("selected", is_selected);
}

/**
 * [Documentation](https://developer.mozilla.org/docs/Web/HTML/Attributes/accept)
 */
export function accept(types) {
  return attribute("accept", $string.join(types, " "));
}

/**
 * [Documentation](https://developer.mozilla.org/docs/Web/API/HTMLFormElement/acceptCharset)
 */
export function accept_charset(types) {
  return attribute("acceptCharset", $string.join(types, " "));
}

export function msg(uri) {
  return attribute("msg", uri);
}

/**
 * [Documentation](https://developer.mozilla.org/docs/Web/HTML/Attributes/autocomplete)
 */
export function autocomplete(name) {
  return attribute("autocomplete", name);
}

/**
 * [Documentation](https://developer.mozilla.org/docs/Web/HTML/Global_attributes/autofocus)
 */
export function autofocus(should_autofocus) {
  return attribute("autofocus", should_autofocus);
}

/**
 * [Documentation](https://developer.mozilla.org/docs/Web/HTML/Attributes/disabled)
 */
export function disabled(is_disabled) {
  return attribute("disabled", is_disabled);
}

/**
 * [`<meta>` Documentation](https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Elements/meta/name) \
 * [`<form>` Documentation](https://developer.mozilla.org/docs/Web/API/HTMLFormElement/name)
 */
export function name(name) {
  return attribute("name", name);
}

/**
 * [Documentation](https://developer.mozilla.org/docs/Web/HTML/Attributes/pattern)
 */
export function pattern(regex) {
  return attribute("pattern", regex);
}

/**
 * [Documentation](https://developer.mozilla.org/docs/Web/HTML/Attributes/readonly)
 */
export function readonly(is_readonly) {
  return attribute("readonly", is_readonly);
}

/**
 * [Documentation](https://developer.mozilla.org/docs/Web/HTML/Attributes/required)
 */
export function required(is_required) {
  return attribute("required", is_required);
}

/**
 * [Documentation](https://developer.mozilla.org/docs/Web/HTML/Attributes/max)
 */
export function max(val) {
  return attribute("max", val);
}

/**
 * [Documentation](https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Attributes/maxlength)
 */
export function max_length(value) {
  return attribute("maxLength", value);
}

/**
 * [Documentation](https://developer.mozilla.org/docs/Web/HTML/Attributes/min)
 */
export function min(val) {
  return attribute("min", val);
}

/**
 * [Documentation](https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Attributes/minlength)
 */
export function min_length(value) {
  return attribute("minLength", value);
}

/**
 * [Documentation](https://developer.mozilla.org/docs/Web/HTML/Attributes/step)
 */
export function step(val) {
  return attribute("step", val);
}

/**
 * [Documentation](https://developer.mozilla.org/docs/Web/API/HTMLTextAreaElement/cols)
 */
export function cols(val) {
  return attribute("cols", val);
}

/**
 * [Documentation](https://developer.mozilla.org/docs/Web/API/HTMLTextAreaElement/rows)
 */
export function rows(val) {
  return attribute("rows", val);
}

/**
 * [Documentation](https://developer.mozilla.org/docs/Web/API/HTMLTextAreaElement/wrap)
 */
export function wrap(mode) {
  return attribute("wrap", mode);
}

/**
 * [Documentation](https://developer.mozilla.org/docs/Web/API/HTMLAnchorElement/download)
 */
export function download(filename) {
  return attribute("download", filename);
}

/**
 * [Documentation](https://developer.mozilla.org/docs/Web/HTML/Attributes/rel)
 */
export function rel(relationship) {
  return attribute("rel", relationship);
}

/**
 * [Documentation](https://developer.mozilla.org/docs/Web/API/HTMLObjectElement/height)
 */
export function height(val) {
  return attribute("height", val);
}

/**
 * [Documentation](https://developer.mozilla.org/docs/Web/API/HTMLObjectElement/width)
 */
export function width(val) {
  return attribute("width", val);
}

/**
 * [Documentation](https://developer.mozilla.org/docs/Web/API/HTMLMediaElement/autoplay)
 */
export function autoplay(should_autoplay) {
  return attribute("autoplay", should_autoplay);
}

/**
 * [Documentation](https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Attributes/size)
 */
export function size(value) {
  return attribute("size", value);
}

/**
 * [Documentation](https://developer.mozilla.org/docs/Web/API/HTMLMediaElement/controls)
 */
export function controls(visible) {
  return attribute("controls", visible);
}

/**
 * [Documentation](https://developer.mozilla.org/docs/Web/API/HTMLMediaElement/loop)
 */
export function loop(should_loop) {
  return attribute("loop", should_loop);
}

/**
 * [Documentation](https://developer.mozilla.org/docs/Web/API/HTMLFormElement/action)
 */
export function action(url) {
  return attribute("action", url);
}

/**
 * [Documentation](https://react.dev/reference/react-dom/components/form#handle-form-submission-on-the-client)
 */
export function action_(handler) {
  return attribute("action", handler);
}

/**
 * [Documentation](https://developer.mozilla.org/docs/Web/API/HTMLFormElement/enctype)
 */
export function enctype(value) {
  return attribute("enctype", value);
}

/**
 * [Documentation](https://developer.mozilla.org/docs/Web/API/HTMLFormElement/method)
 */
export function method(method) {
  return attribute("method", method);
}

/**
 * [Documentation](https://developer.mozilla.org/docs/Web/API/HTMLFormElement/novalidate)
 */
export function novalidate(value) {
  return attribute("novalidate", value);
}

/**
 * [Documentation](https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Attributes/form)
 */
export function form(value) {
  return attribute("form", value);
}

/**
 * [Documentation](https://developer.mozilla.org/docs/Web/HTML/Element/button#formaction)
 */
export function form_action(action) {
  return attribute("formAction", action);
}

/**
 * [Documentation](/// [Documentation](https://react.dev/reference/react-dom/components/form#handle-form-submission-on-the-client)
 */
export function form_action_(handler) {
  return attribute("formAction", handler);
}

/**
 * [Documentation](https://developer.mozilla.org/docs/Web/HTML/Element/button#formenctype)
 */
export function form_enctype(value) {
  return attribute("formenctype", value);
}

/**
 * [Documentation](https://developer.mozilla.org/docs/Web/HTML/Element/button#formmethod)
 */
export function form_method(method) {
  return attribute("formmethod", method);
}

/**
 * [Documentation](https://developer.mozilla.org/docs/Web/HTML/Element/button#formnovalidate)
 */
export function form_novalidate(value) {
  return attribute("formnovalidate", value);
}

/**
 * [Documentation](https://developer.mozilla.org/docs/Web/HTML/Element/button#formtarget)
 */
export function form_target(target) {
  return attribute("formtarget", target);
}

/**
 * [Documentation](https://developer.mozilla.org/docs/Web/API/HTMLDetailsElement/open)
 */
export function open(is_open) {
  return attribute("open", is_open);
}

/**
 * [Documentation](https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Global_attributes/lang)
 */
export function nonce(nonce) {
  return attribute("nonce", nonce);
}

/**
 * [Documentation](https://react.dev/learn/rendering-lists)
 */
export function key(key) {
  return attribute("key", key);
}

/**
 * [Documentation](https://developer.mozilla.org/en-US/docs/Web/API/HTMLTableCellElement/colSpan)
 */
export function col_span(value) {
  return attribute("colSpan", value);
}

/**
 * [Documentation](https://developer.mozilla.org/en-US/docs/Web/API/HTMLTableCellElement/rowSpan)
 */
export function row_span(value) {
  return attribute("rowSpan", value);
}

/**
 * `none` will not appear in HTML attributes. Use it for conditional rendering.
 */
export function none() {
  return attribute("none_", undefined);
}
